/**
 * Authentication Context
 *
 * Wraps react-oidc-context for Keycloak OIDC authentication.
 * Provides a consistent useAuth() interface for the app.
 * Supports bypass/mock modes for development.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import { useAuth as useOidcAuth } from "react-oidc-context"

import type { ReactNode } from "react"

import { env } from "@/shared/config/env"
import { userData } from "@/shared/mocks/user"
import mockUser from "@/shared/mocks/mock-user.json"

/**
 * Authenticated user type
 */
export interface AuthUser {
  id: string
  sicilNo?: number | null
  email: string | null
  name: string
  avatar: string | null
  status: string
}

/**
 * Auth context public interface
 */
interface AuthContextType {
  user: AuthUser | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  login: () => void
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Storage keys
const AUTH_STORAGE_KEY = "auth_token"
const USER_STORAGE_KEY = "auth_user"

// Bypass user for development without backend
const BYPASS_USER: AuthUser = {
  id: userData.id,
  email: userData.email,
  name: userData.name,
  avatar: userData.avatar,
  status: userData.status,
}
const BYPASS_TOKEN = "dev-bypass-token"

const MOCK_USER: AuthUser = {
  id: mockUser.id,
  email: mockUser.email,
  name: mockUser.name,
  avatar: mockUser.avatar || null,
  status: mockUser.status || "ONLINE",
}
const MOCK_TOKEN = "dev-mock-token"

function parseSicilNo(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return Math.trunc(value)
  if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    return Number.parseInt(value.trim(), 10)
  }
  return null
}

function extractSicilNoFromProfile(profile: Record<string, unknown>): number | null {
  const keys = [
    "sicilNo",
    "sicil_no",
    "sicilno",
    "employeeNumber",
    "employee_number",
    "personnelNumber",
    "personnel_number",
    "preferred_username",
    "sub",
  ]

  for (const key of keys) {
    const parsed = parseSicilNo(profile[key])
    if (parsed !== null) return parsed
  }

  return null
}

/**
 * Auth provider for bypass/mock modes (no OIDC)
 */
function BypassAuthProvider({ children }: { children: ReactNode }) {
  const user = env.VITE_ENABLE_MOCK_API ? MOCK_USER : BYPASS_USER
  const token = env.VITE_ENABLE_MOCK_API ? MOCK_TOKEN : BYPASS_TOKEN

  useEffect(() => {
    localStorage.setItem(AUTH_STORAGE_KEY, token)
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user))
  }, [user, token])

  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_STORAGE_KEY)
    localStorage.removeItem(USER_STORAGE_KEY)
    window.location.href = "/"
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
        login: () => {},
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

/**
 * Auth provider for OIDC mode (wraps react-oidc-context)
 */
function OidcAuthProvider({ children }: { children: ReactNode }) {
  const oidc = useOidcAuth()

  const [authUser, setAuthUser] = useState<AuthUser | null>(null)

  // Map OIDC user profile to AuthUser and sync token to localStorage
  useEffect(() => {
    if (oidc.user) {
      const profile = oidc.user.profile
      const profileRecord = profile as unknown as Record<string, unknown>
      const mappedUser: AuthUser = {
        id: profile.sub,
        sicilNo: extractSicilNoFromProfile(profileRecord),
        name: (profile.name as string) || (profile.preferred_username as string) || "",
        email: (profile.email as string) || null,
        avatar: null,
        status: "ONLINE",
      }
      setAuthUser(mappedUser)

      // Sync token to localStorage for axios interceptor
      localStorage.setItem(AUTH_STORAGE_KEY, oidc.user.access_token)
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(mappedUser))
    } else {
      setAuthUser(null)
      localStorage.removeItem(AUTH_STORAGE_KEY)
      localStorage.removeItem(USER_STORAGE_KEY)
    }
  }, [oidc.user])

  const login = useCallback(() => {
    const params = new URLSearchParams(window.location.search)
    const redirectTo = params.get("redirectTo") || "/"
    oidc.signinRedirect({ state: { redirectTo } }).catch((err) => {
      console.error("[OIDC] signinRedirect error:", err)
    })
  }, [oidc])

  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_STORAGE_KEY)
    localStorage.removeItem(USER_STORAGE_KEY)
    oidc.signoutRedirect({
      post_logout_redirect_uri: window.location.origin,
    })
  }, [oidc])

  return (
    <AuthContext.Provider
      value={{
        user: authUser,
        token: oidc.user?.access_token ?? null,
        isAuthenticated: oidc.isAuthenticated,
        isLoading: oidc.isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

/**
 * Main AuthProvider - delegates to bypass or OIDC based on env
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  if (env.VITE_AUTH_BYPASS) {
    return <BypassAuthProvider>{children}</BypassAuthProvider>
  }

  return <OidcAuthProvider>{children}</OidcAuthProvider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
